import { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { maintenanceManagementActions } from '../../../store/reducers/maintenance-management';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { IconButton, Stack, Typography, useTheme } from '@mui/material';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../../utils/table-utils';
import { ReactComponent as ViewIcon } from '../../../assets/icons/main/view-icon.svg';
import { get } from 'lodash';
import { MaintenanceManagementHistoryPayloadTypes } from '../../../store/models/machinery-management';
import MasterComponent from '../../../common/MasterComponent';
import { pageActions } from '../../../store/reducers/page-actions';
import dayjs from 'dayjs';

interface MaintenanceHistoryTypes {
  isMaintenanceHistoryLoading: false,
  isMaintenanceHistoryHaveError: false,
  maintenanceHistoryData: Array<any>;
  maintenanceHistoryTotalCount: 0,
  maintenanceHistoryPageNumber: 0,
  maintenanceHistorySearchItem: '',
  maintenanceName: '',
}

const MaintenanceHistory = ({
  isMaintenanceHistoryLoading,
  maintenanceHistoryData,
  maintenanceHistoryTotalCount,
  maintenanceHistoryPageNumber,
  maintenanceName,
}: MaintenanceHistoryTypes) => {
  const dispatch = useDispatch();
  const Navigate = useNavigate();
  const theme = useTheme();
  const themeMode: any = get(useOutletContext(), 'themeMode', {});
  const [searchValue, setSearchValue] = useState<any>('')
  const [sortingModel, setSortingModel] = useState({ field: '', sort: '' });
  const params = useParams();
  const mainId = get(params, 'maintId', '');
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname.length) {
      const pathnamesTemp: any = pathname.split('/').filter(Boolean);
      if (pathnamesTemp.length) {
        const pathnamesData: any = pathnamesTemp
          .filter((i: any, index: number) => index != 2)
          .map((item: any, index: number) => {
            if (index == 0) {
              return { route: pathnamesTemp[0], pathName: 'Maintenance Management', navigate: true };
            } else if (index == 1) {
              return { route: '', pathName: maintenanceName, navigate: false };
            } else {
              return { route: '', pathName: 'Maintenance History', navigate: false };
            }
          });
        dispatch(pageActions.setPagePath(pathnamesData));
        return () => {
          const data: any = [];
          dispatch(pageActions.setPagePath(data));
        };
      }
    }
  }, [pathname, maintenanceName]);

  const handleViewAction = (params: any) => {
    Navigate(`/maintenance-management/history/${params.mt_id}/activity/${params.task_id}`)
  }

  const dipatchMaintenanceHistoryListRequest = (payload: MaintenanceManagementHistoryPayloadTypes) => {
    dispatch(
      maintenanceManagementActions.getMaintenanceHistoryRequest({
        ...sortingModel,
        search_item: searchValue,
        ...payload,
        maintId: mainId
      })
    );
  };

  useEffect(() => {
    if (mainId) {
      dipatchMaintenanceHistoryListRequest({
        limit: DEFAULT_PAGE_SIZE,
        page: DEFAULT_PAGE_NUMBER,
      });
    }
  }, [mainId]);


  const columns: any = [
    {
      field: 'task_completed_date',
      headerName: 'Maintenance date',
      flex: 1,
      minWidth: 450,
      headerAlign: 'left',
      align: 'left',
      sortable: 'true',
      renderCell: (params: any) => {
        return (
          <Typography
            component='p'
            variant='subtitle1'
            sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
          >
            {params.value ? dayjs.utc(params.value).local().format('MMM DD, YYYY hh:mm A') : dayjs.utc(params.row.current_task_date).local().format('MMM DD, YYYY hh:mm A')}
          </Typography>
        );
      },
    },
    {
      field: 'task_remarks',
      headerName: 'Remark',
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      minWidth: 450,
      sortable: 'true',
    },
    {
      field: 'view_activity_details',
      headerName: 'View activity details',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      minWidth: 300,
      sortable: false,
      renderCell: (params: any) => {
        const disableUpdate = false;
        return (
          <IconButton disableRipple onClick={() => handleViewAction(params.row)} disabled={disableUpdate}>
            <ViewIcon
              style={{
                cursor: 'pointer',
                width: 24,
                height: 24,
                opacity: disableUpdate ? 0.5 : 1,
              }}
            />
          </IconButton>
        );
      },
    },
  ];
  return (
    <>
      <MasterComponent
        heading={`Maintenance ${maintenanceName} History`}
        isLoading={isMaintenanceHistoryLoading}
        rows={maintenanceHistoryData}
        columns={columns}
        isServerPagination
        totalRowCount={maintenanceHistoryTotalCount}
        pageNumber={maintenanceHistoryPageNumber}
        onPagination={(pageSize: number, pageNumber: number) => {
          dipatchMaintenanceHistoryListRequest({
            limit: pageSize,
            page: pageNumber,
          });
        }}
        noRowsOverlay={() => (
          <Stack height='100%' alignItems='center' justifyContent='center'>
            Maintenance History not available
          </Stack>
        )}
        onSorting={(sortModel: { field: string; sort: string }) => {
          dipatchMaintenanceHistoryListRequest({
            limit: DEFAULT_PAGE_SIZE,
            page: maintenanceHistoryPageNumber,
            ...sortModel,
          });
          setSortingModel(sortModel);
        }}
        showSearch
        searchPlaceholder='Search'
        onSearch={(value: string) => {
          dipatchMaintenanceHistoryListRequest({
            limit: DEFAULT_PAGE_SIZE,
            page: DEFAULT_PAGE_NUMBER,
            search_item: value,
          });
          setSearchValue(value);
          // dispatch(maintenanceManagementActions.onSearchForMaintenanceItem(searchValue));
        }}
        hideAddNewButton
        otherProps={{
          getRowId: (row: any) => row.id,
        }}
      />
    </>
  );
};

const mapStateToProps = (state: any) => ({
  isMaintenanceHistoryLoading: state.maintenanceManagement.isMaintenanceHistoryLoading,
  isMaintenanceHistoryHaveError: state.maintenanceManagement.isMaintenanceHistoryHaveError,
  maintenanceHistoryData: state.maintenanceManagement.maintenanceHistoryData.map((item:any, index:number)=> ({...item,id:index})),
  maintenanceHistoryTotalCount: state.maintenanceManagement.maintenanceHistoryTotalCount,
  maintenanceHistoryPageNumber: state.maintenanceManagement.maintenanceHistoryPageNumber,
  maintenanceHistorySearchItem: state.maintenanceManagement.maintenanceHistorySearchItem,
  maintenanceName: state.maintenanceManagement.maintenanceName,
});

export default connect(mapStateToProps)(MaintenanceHistory);
